import React from "react";
import _ from "lodash";
import { checkIsMobile, getCalcWindowSize } from "core/utils/utils";

/**
 * Custom hooks to create canvas for component to consume
 * @param {Object} defaultSizeSetting
 * @param {Function} getCanvasOptions () => ({ myCanvasInstance, ref, registerCanvasEvents = true, autoResize = true })
 * @returns {Array} [myCanvas, size, defaultSize]
 */
const useCustomCanvas = (defaultSizeSetting, getCanvasOptions) => {
    const [myCanvas, setMyCanvas] = React.useState(null);

    const defaultSizeSettingRef = React.useRef(defaultSizeSetting);
    const defaultSize = React.useMemo(() => getCalcWindowSize(defaultSizeSetting), [defaultSizeSetting]);
    const [size, setSize] = React.useState(defaultSize);

    React.useEffect(() => {
        defaultSizeSettingRef.current = defaultSizeSetting;
    }, [defaultSizeSetting]);

    const setDefaultSize = (myCanvasInstance, size) => {
        if (myCanvasInstance && size) {
            myCanvasInstance.setSize(size.width, size.height, true);
            setSize(size);
        }
    };

    React.useEffect(() => {
        const { myCanvasInstance, ref, registerCanvasEvents = true, autoResize = true } = getCanvasOptions();
        setMyCanvas(myCanvasInstance);

        const eventsPool = [];
        //register canvas event
        if (registerCanvasEvents) {
            for (let eventKey in myCanvasInstance.eventHandlers) {
                ref.current?.addEventListener(eventKey, myCanvasInstance.eventHandlers[eventKey]);
                eventsPool.push({ el: ref.current, key: eventKey, func: myCanvasInstance.eventHandlers[eventKey] });
            }
        }

        //register resize event
        if (autoResize) {
            const onResize = _.debounce(() => {
                setDefaultSize(myCanvasInstance, getCalcWindowSize(defaultSizeSettingRef.current));
            }, 150);

            const resizeEvent = { el: window, key: null, func: onResize };
            if (checkIsMobile()) {
                window.addEventListener("orientationchange", onResize);
                resizeEvent.eventKey = "orientationchange";
            } else {
                window.addEventListener("resize", onResize);
                resizeEvent.eventKey = "resize";
            }
            eventsPool.push(resizeEvent);
        }

        return () => {
            //clean up all the registered event
            for (let event of eventsPool) {
                event.el.removeEventListener(event.key, event.func);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setMyCanvas]);

    React.useEffect(() => {
        setDefaultSize(myCanvas, defaultSize);
    }, [myCanvas, defaultSize]);

    return [myCanvas, size, defaultSize];
};

export default useCustomCanvas;
